import React, { useState, useMemo } from 'react'
import { IoChevronDown, IoChevronUp, IoInformationCircleOutline } from 'react-icons/io5'

interface NarrativeSummaryProps {
  narrative: string
  maxLength?: number
  showTitle?: boolean
  variant?: 'default' | 'compact' | 'detailed'
}

export function NarrativeSummary({
  narrative,
  maxLength = 200,
  showTitle = true,
  variant = 'default',
}: NarrativeSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  // Parse narrative into sentences for better formatting
  const sentences = useMemo(() => {
    if (!narrative) return []
    // Split by sentence endings but keep the punctuation
    return narrative
      .split(/(?<=[.!?])\s+/)
      .filter((s) => s.trim().length > 0)
  }, [narrative])

  // Determine if truncation is needed
  const needsTruncation = narrative.length > maxLength

  // Get display text - either full or truncated
  const displayText = useMemo(() => {
    if (isExpanded || !needsTruncation) {
      return narrative
    }
    // Truncate at sentence boundary if possible
    let truncated = ''
    for (const sentence of sentences) {
      if (truncated.length + sentence.length > maxLength) {
        break
      }
      truncated += (truncated ? ' ' : '') + sentence
    }
    // If no complete sentence fits, use character truncation
    if (!truncated && narrative.length > 0) {
      truncated = narrative.slice(0, maxLength).trim()
      // Try to break at word boundary
      const lastSpace = truncated.lastIndexOf(' ')
      if (lastSpace > maxLength * 0.6) {
        truncated = truncated.slice(0, lastSpace)
      }
    }
    return truncated + (truncated.length < narrative.length ? '...' : '')
  }, [narrative, sentences, isExpanded, needsTruncation, maxLength])

  // Extract key highlights from narrative (numbers, percentages)
  const highlights = useMemo(() => {
    const numbers = narrative.match(/\d+\.?\d*\s*(?:cards?|yellow|%|matches?)/gi) || []
    return numbers.slice(0, 3)
  }, [narrative])

  // Check if this is a positive or negative outlook
  const sentiment = useMemo(() => {
    const lower = narrative.toLowerCase()
    if (lower.includes('elevated') || lower.includes('high') || lower.includes('above')) {
      return 'elevated'
    }
    if (lower.includes('reduced') || lower.includes('low') || lower.includes('below')) {
      return 'reduced'
    }
    return 'neutral'
  }, [narrative])

  if (!narrative || narrative === 'No prediction data available.') {
    return (
      <div className="cards-narrative cards-narrative-empty">
        <div className="cards-narrative-header">
          {showTitle && (
            <h3 className="cards-narrative-title">
              <IoInformationCircleOutline size={16} />
              Analysis Unavailable
            </h3>
          )}
        </div>
        <p className="cards-narrative-text muted">
          Card analysis data is not available for this fixture.
        </p>
      </div>
    )
  }

  if (variant === 'compact') {
    return (
      <div className="cards-narrative cards-narrative-compact">
        <p className="cards-narrative-text">{displayText}</p>
      </div>
    )
  }

  return (
    <div className={`cards-narrative cards-narrative-${sentiment}`}>
      {showTitle && (
        <div className="cards-narrative-header">
          <h3 className="cards-narrative-title">Why This Bet</h3>
          {highlights.length > 0 && variant === 'detailed' && (
            <div className="cards-narrative-highlights">
              {highlights.map((h, i) => (
                <span key={i} className="cards-narrative-highlight">
                  {h}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="cards-narrative-content">
        <p className="cards-narrative-text">{displayText}</p>
      </div>

      {needsTruncation && (
        <button
          type="button"
          className="cards-narrative-toggle"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
        >
          {isExpanded ? (
            <>
              <span>Show Less</span>
              <IoChevronUp size={14} />
            </>
          ) : (
            <>
              <span>Read More</span>
              <IoChevronDown size={14} />
            </>
          )}
        </button>
      )}
    </div>
  )
}
